"use client";

import { useEffect, useState } from "react";
import {
  useSimulation,
  SavedSimulation,
} from "@/components/simulation-context";
import { useI18n } from "@/lib/i18n/hook";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";

// Constants
const CHART_COLORS = {
  blue: "#3b82f6",
  green: "#10b981",
  orange: "#f97316",
  success: "#22c55e",
  warning: "#eab308",
  error: "#ef4444",
};

// Components
function NoSelectionState() {
  const { t } = useI18n();
  return (
    <div className="text-center py-12">
      <h3 className="text-lg font-medium mb-2">
        {t("riskSimulation.noSelectedSimulations")}
      </h3>
      <p className="text-muted-foreground">
        {t("riskSimulation.selectTwoOrMoreSimulations")}
      </p>
    </div>
  );
}

// Main Component
export function ComparisonChart() {
  const { savedSimulations, selectedSimulationIds, clearSelectedSimulations } =
    useSimulation();
  const { t } = useI18n();
  const [selectedSimulations, setSelectedSimulations] = useState<
    SavedSimulation[]
  >([]);

  useEffect(() => {
    const selected = savedSimulations.filter((sim) =>
      selectedSimulationIds.includes(sim.id)
    );
    setSelectedSimulations(selected);
  }, [savedSimulations, selectedSimulationIds]);

  if (selectedSimulations.length < 2) {
    return <NoSelectionState />;
  }

  // データ整形: 資産比較チャート用
  const assetData = selectedSimulations.map((sim) => ({
    name: sim.name,
    mean: sim.result.statistics.mean,
    median: sim.result.statistics.median,
    totalInvestment:
      sim.params.investmentAmount * sim.params.investmentPeriod * 12 +
      sim.params.initialInvestment,
  }));

  // データ整形: 成功率チャート用
  const successRateData = selectedSimulations.map((sim) => ({
    name: sim.name,
    successRate: sim.result.successRate,
  }));

  const getSuccessRateColor = (rate: number) => {
    if (rate >= 70) return CHART_COLORS.success;
    if (rate >= 40) return CHART_COLORS.warning;
    return CHART_COLORS.error;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">
          {t("riskSimulation.comparisonResults")}
        </h2>
        <Button variant="outline" onClick={clearSelectedSimulations}>
          {t("common.clearSelection")}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 最終資産比較チャート */}
        <Card>
          <CardHeader>
            <CardTitle>{t("riskSimulation.finalAssetComparison")}</CardTitle>
            <CardDescription>
              {t("riskSimulation.finalAssetComparisonDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={assetData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis tickFormatter={(val) => `¥${val.toLocaleString()}`} />
                  <Tooltip
                    formatter={(value: number) => `¥${value.toLocaleString()}`}
                  />
                  <Legend />
                  <Bar
                    dataKey="mean"
                    name={t("common.statistics.mean")}
                    fill={CHART_COLORS.blue}
                  />
                  <Bar
                    dataKey="median"
                    name={t("common.statistics.medianFinalAsset")}
                    fill={CHART_COLORS.green}
                  />
                  <Bar
                    dataKey="totalInvestment"
                    name={t("common.statistics.totalInvestment")}
                    fill={CHART_COLORS.orange}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* 成功率比較チャート */}
        <Card>
          <CardHeader>
            <CardTitle>{t("riskSimulation.successRateComparison")}</CardTitle>
            <CardDescription>
              {t("riskSimulation.successRateComparisonDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={successRateData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis domain={[0, 100]} unit="%" />
                  <Tooltip formatter={(value: number) => `${value.toFixed(1)}%`} />
                  <Bar
                    dataKey="successRate"
                    name={t("common.statistics.targetAchieveRate")}
                  >
                    {successRateData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={getSuccessRateColor(entry.successRate)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* パラメータ一覧テーブル */}
      <Card>
        <CardHeader>
          <CardTitle>{t("riskSimulation.simulationParameters")}</CardTitle>
          <CardDescription>
            {t("riskSimulation.simulationParametersDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("riskSimulation.simulationName")}</TableHead>
                <TableHead>{t("riskSimulation.initialInvestment")}</TableHead>
                <TableHead>{t("riskSimulation.monthlyInvestment")}</TableHead>
                <TableHead>{t("riskSimulation.investmentPeriod")}</TableHead>
                <TableHead>{t("riskSimulation.annualReturnRate")}</TableHead>
                <TableHead>{t("riskSimulation.volatility")}</TableHead>
                <TableHead>{t("riskSimulation.targetAmount")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {selectedSimulations.map((sim) => (
                <TableRow key={sim.id}>
                  <TableCell>{sim.name}</TableCell>
                  <TableCell>
                    {sim.params.initialInvestment.toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {sim.params.investmentAmount.toLocaleString()}
                  </TableCell>
                  <TableCell>{sim.params.investmentPeriod}</TableCell>
                  <TableCell>{sim.params.returnRate}%</TableCell>
                  <TableCell>{sim.params.volatility}%</TableCell>
                  <TableCell>
                    {sim.params.targetAmount.toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
